import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { LetDirective, PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  NEVER,
  Observable,
  Subject,
} from 'rxjs';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { FullCandidateSelectors } from '@recruitee/candidate-data';
import { filterDefined } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import type { OfferBrief } from '@recruitee/offers-types';
import { OfferSimpleListItem } from '@recruitee/offers-ui';
import type { TalentPoolBrief } from '@recruitee/talent-pools-types';
import {
  AvatarComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  RadioFormGroupComponent,
  ScrollableDirective,
  ScrollTrapDirective,
  SpinnerComponent,
  TruncateComponent,
} from '@recruitee/user-interface';

import {
  AccessOptionsConfig,
  AccessValue,
  SHARE_ON_THE_GO_I18N,
  ShareOnTheGoDialogData,
  ShareOnTheGoDialogResult,
  ShareOnTheGoI18n,
  UserAccessValue,
} from './types';
import { UserItemComponent } from './user-item/user-item.component';

type ShareOnTheGoDialogComponentData = ShareOnTheGoDialogData & { allSelectedUsers: User[] };

@Component({
  selector: 'rt-share-on-the-go-dialog',
  templateUrl: './share-on-the-go-dialog.component.html',
  styleUrls: ['./share-on-the-go-dialog.component.less'],
  standalone: true,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    TranslatePipe,
    ButtonComponent,
    BoxComponent,
    AvatarComponent,
    HeadingComponent,
    TruncateComponent,
    DividerComponent,
    RadioFormGroupComponent,
    ReactiveFormsModule,
    PushPipe,
    LetDirective,
    SpinnerComponent,
    UserItemComponent,
    IconComponent,
    ScrollableDirective,
    ScrollTrapDirective,
    ButtonLoadingDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareOnTheGoDialogComponent
  implements DialogComponentI<ShareOnTheGoDialogComponentData, ShareOnTheGoDialogResult>, OnInit
{
  public data: ShareOnTheGoDialogComponentData | null = null;
  public offer$: Observable<OfferSimpleListItem> | null = null;
  public usersWithAccess$: Observable<User[]> | null = null;
  public usersWithAccessOpen$ = new BehaviorSubject<boolean>(false);
  public accessOptionsConfigs$: Observable<AccessOptionsConfig[]> | null = null;
  public loading$ = new Subject<boolean>();
  public submitEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  @ViewChildren(UserItemComponent)
  private userItems: QueryList<UserItemComponent>;

  private currentUserAccessibleOfferIds$ = this.store.select(CurrentUserSelectors.data).pipe(
    filterDefined,
    map(user => user.membership?.offerIds ?? []),
  );

  private get accessValues(): UserAccessValue[] {
    return <UserAccessValue[]>this.userItems
        ?.filter(userItem => userItem.selectedOption.value !== null)
        ?.map(userItem => ({
          user: userItem.user,
          accessValue: userItem.selectedOption.value,
        })) ?? [];
  }

  constructor(
    private store: Store,
    private dialogRef: DialogRef<ShareOnTheGoDialogComponent>,
    @Inject(SHARE_ON_THE_GO_I18N) public shareOnTheGoI18n: ShareOnTheGoI18n,
  ) {}

  public trackById<T extends { id: number }>(_: number, item: T): number {
    return item.id;
  }

  public setDialogData(data: ShareOnTheGoDialogComponentData): void {
    this.data = data;
  }

  public ngOnInit(): void {
    this.offer$ = this.getOffer$();
    this.usersWithAccess$ = this.getUsersWithAccess$();
    this.accessOptionsConfigs$ = this.getAccessOptionsConfigs$();
  }

  public submit(): void {
    this.loading$.next(true);
    this.dialogRef.close({ userAccessValues: this.accessValues });
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public onUserItemValueChanges(): void {
    if (!this.data?.disableSubmitOnSkipAll) {
      return;
    }

    const noSelectedUsersHaveAccess =
      this.data.allSelectedUsers.length === this.data.usersToShareWith.length;
    const skipAllUsers: boolean = this.accessValues.length
      ? this.accessValues.every(
          userAccessValue => userAccessValue.accessValue === AccessValue.DontRequest,
        ) && noSelectedUsersHaveAccess
      : false;

    this.submitEnabled$.next(!skipAllUsers);
  }

  private buildAccessOptionsConfig({
    abilityMap,
    currentUserAccessibleOfferIds,
    userToShareWith,
  }: {
    abilityMap: Record<Ability, boolean>;
    currentUserAccessibleOfferIds: number[];
    userToShareWith: User;
  }): AccessOptionsConfig | never {
    if (!abilityMap[Ability.ShareCandidates] && !abilityMap[Ability.AssignUsersToJobs]) {
      throw new Error('User does not have access to share on the go, but dialog was opened!');
    }
    return {
      [AccessValue.CandidateOnly]: {
        isEnabled: abilityMap[Ability.ShareCandidates],
        isVisible: true,
      },
      [AccessValue.AllCandidatesInOffer]: {
        isEnabled:
          abilityMap[Ability.AssignUsersToJobs] &&
          (currentUserAccessibleOfferIds.includes(this.data!.offerId ?? -1) ||
            abilityMap[Ability.ViewAllJobs]) &&
          userToShareWith.roleAbilities.includes(Ability.ViewJobs),
        isVisible: true,
      },
      [AccessValue.DontRequest]: {
        isEnabled: true,
        isVisible: (this.data?.usersToShareWith?.length ?? 0) > 1,
      },
    };
  }

  private getOffer$(): Observable<OfferBrief | TalentPoolBrief> | null {
    return <Observable<OfferBrief | TalentPoolBrief>>this.store
      .select(
        FullCandidateSelectors.currentUserAvailablePlacements({
          candidateId: this.data!.candidateId,
        }),
      )
      .pipe(
        map(placements => {
          const placement = placements.find(
            placement => (placement.offerId || placement.talentPoolId) === this.data!.offerId,
          );
          return placement?.offer || placement?.talentPool;
        }),
        filter(offer => !!offer),
      );
  }

  private getAccessOptionsConfigs$(): Observable<AccessOptionsConfig[]> | null {
    return combineLatest([
      this.store.select(AbilitiesSelectors.abilityMap),
      this.currentUserAccessibleOfferIds$,
    ]).pipe(
      map(
        ([abilityMap, currentUserAccessibleOfferIds]) =>
          this.data?.usersToShareWith?.map(userToShareWith =>
            this.buildAccessOptionsConfig({
              abilityMap,
              currentUserAccessibleOfferIds,
              userToShareWith,
            }),
          ) ?? [],
      ),
      catchError(err => {
        console.warn(err.message);
        this.cancel();
        return NEVER;
      }),
    );
  }

  private getUsersWithAccess$(): Observable<User[]> | null {
    return combineLatest([
      this.store.select(
        FullCandidateSelectors.offerUsersWithAccess({
          candidateId: this.data!.candidateId,
          offerId: this.data?.offerId ?? -1,
        }),
      ),
      this.store.select(CurrentUserSelectors.data).pipe(filterDefined),
    ]).pipe(
      map(([usersWithAccess, currentUser]) =>
        usersWithAccess.filter(
          userWithAccess =>
            this.wasUserSelected(userWithAccess) && userWithAccess.id !== currentUser.id,
        ),
      ),
    );
  }

  private wasUserSelected(userWithAccess: User): boolean {
    return !!this.data?.allSelectedUsers
      .map(userToShareWith => userToShareWith.id)
      ?.includes(userWithAccess.id);
  }
}
